import React, { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Typography,
  Checkbox,
  IconButton,
  Collapse,
  Link,
  Menu,
  MenuItem,
  Tooltip,
  Divider,
  ListItemIcon,
  ListItemText
} from '@mui/material'
import {
  KeyboardArrowDown,
  KeyboardArrowRight,
  Language,
  PersonSearch,
  AlternateEmail,
  MailOutline,
  Visibility,
  DeleteOutline,
  MoreVert
} from '@mui/icons-material'
import DartIcon from '../atoms/DartIcon'

const RELEVANCY_CONFIG = {
  HIGHLY_SUITABLE: { level: '1', color: '#1B5E20', tooltipKey: 'mailTemplate.campaigns.dashboard.firmRow.highlyRelevant' },
  MODERATELY_SUITABLE: { level: '2', color: '#388E3C', tooltipKey: 'mailTemplate.campaigns.dashboard.firmRow.moderatelyRelevant' },
  MINIMALLY_SUITABLE: { level: '3', color: '#8BC34A', tooltipKey: 'mailTemplate.campaigns.dashboard.firmRow.minimallyRelevant' },
  NOT_SUITABLE: { level: 'X', color: '#F44336', tooltipKey: 'mailTemplate.campaigns.dashboard.firmRow.notRelevant' },
  CANNOT_ACCESS: { level: 'N/A', color: '#660101', tooltipKey: 'mailTemplate.campaigns.dashboard.firmRow.cannotAccess' }
}

const RELEVANCY_STATUS = {
  NOT_STARTED: { labelKey: 'mailTemplate.campaigns.dashboard.firmRow.notChecked', color: '#9E9E9E' },
  IN_PROGRESS: { labelKey: 'mailTemplate.campaigns.dashboard.firmRow.checking', color: '#1565C0' },
  DONE: (level) => {
    const config = RELEVANCY_CONFIG[level]
    return config || { labelKey: 'mailTemplate.campaigns.dashboard.firmRow.checked', color: '#2E7D32' }
  }
}

const CONTENT_STATUS = {
  NOT_STARTED: { labelKey: 'mailTemplate.campaigns.dashboard.firmRow.notGenerated', color: '#9E9E9E' },
  IN_PROGRESS: { labelKey: 'mailTemplate.campaigns.dashboard.firmRow.generating', color: '#1565C0' },
  DONE: (readyCount) => ({
    labelKey: 'mailTemplate.campaigns.dashboard.firmRow.readyCount',
    labelParams: { count: readyCount },
    color: readyCount > 0 ? '#2E7D32' : '#E65100'
  })
}

const extractWebsite = (web) => {
  if (!web) return null
  const url = web.split(',')[0]?.trim()
  return url || null
}

const StatusCard = ({ icon: Icon, label, labelKey, labelParams, color }) => {
  const { t } = useTranslation()
  const displayLabel = labelKey ? t(labelKey, labelParams) : label

  return (
    <Box
      sx={{
        px: 1.25,
        py: 0.75,
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        width: 110
      }}
    >
      {Icon && <Icon sx={{ fontSize: 18, color, flexShrink: 0 }} />}
      <Typography
        variant="caption"
        sx={{ color, fontSize: '0.75rem', fontWeight: 500, whiteSpace: 'nowrap' }}
      >
        {displayLabel}
      </Typography>
    </Box>
  )
}

const RelevancyBadge = ({ badge }) => {
  const { t } = useTranslation()
  if (!badge) return <Box sx={{ width: 140 }} />

  const showSquare = Boolean(badge.level)
  const tooltipText = badge.tooltipKey ? t(badge.tooltipKey) : (badge.labelKey ? t(badge.labelKey, badge.labelParams) : '')

  return (
    <Tooltip
      title={tooltipText}
      arrow
      placement="bottom"
      slotProps={{ popper: { modifiers: [{ name: 'offset', options: { offset: [-40, 4] } }] } }}
    >
      <Box
        sx={{
          px: 1.25,
          py: 0.75,
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          width: 140
        }}
      >
        <DartIcon sx={{ fontSize: 20, flexShrink: 0 }} />
        {showSquare
          ? (
            <Box
              sx={{
                backgroundColor: badge.color,
                color: 'white',
                width: 20,
                height: 20,
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: badge.level === 'N/A' ? '0.5rem' : '0.75rem',
                fontWeight: 'bold',
                flexShrink: 0
              }}
            >
              {badge.level}
            </Box>
            )
          : (
            <Typography
              variant="caption"
              sx={{ color: badge.color, fontSize: '0.75rem', fontWeight: 500, whiteSpace: 'nowrap' }}
            >
              {badge.labelKey ? t(badge.labelKey, badge.labelParams) : badge.label}
            </Typography>
            )
        }
      </Box>
    </Tooltip>
  )
}

const ContactListSection = ({ icon: Icon, title, contacts, onToggle, emailOnly = false }) => {
  const { t } = useTranslation()
  const allSelected = contacts.every(c => c.selected)

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, pt: 1.25, pb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Icon sx={{ fontSize: 14, color: 'text.secondary' }} />
          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', letterSpacing: 0.5, fontSize: '0.7rem' }}>
            {title}
          </Typography>
        </Box>
        <Typography
          variant="caption"
          onClick={(e) => {
            e.stopPropagation()
            contacts.forEach(c => {
              if (allSelected ? c.selected : !c.selected) onToggle?.(c.id)
            })
          }}
          sx={{ color: 'primary.main', cursor: 'pointer', fontSize: '0.7rem', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}
        >
          {allSelected ? t('mailTemplate.campaigns.dashboard.firmRow.deselectAll') : t('mailTemplate.campaigns.dashboard.firmRow.selectAll')}
        </Typography>
      </Box>
      {contacts.map((contact, idx) => {
        const rawName = [contact.firstName, contact.lastName].filter(Boolean).join(' ')
        const hasEmail = Boolean(contact.emailAddress)
        return (
          <MenuItem
            key={contact.id}
            onClick={(e) => { e.stopPropagation(); onToggle?.(contact.id) }}
            dense
            sx={{
              py: 0.75,
              px: 1.5,
              mx: 0.5,
              borderRadius: 1,
              mb: idx === contacts.length - 1 ? 0.5 : 0,
              backgroundColor: contact.selected ? 'rgba(15, 198, 185, 0.06)' : 'transparent',
              '&:hover': { backgroundColor: contact.selected ? 'rgba(15, 198, 185, 0.1)' : 'rgba(0,0,0,0.04)' }
            }}
          >
            <Checkbox size="small" checked={Boolean(contact.selected)} sx={{ p: 0.25, mr: 1 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {emailOnly
                ? (
                  <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: contact.selected ? 500 : 400 }}>
                    {contact.emailAddress || '\u2014'}
                  </Typography>
                  )
                : (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: contact.selected ? 500 : 400, ...(!rawName && { fontStyle: 'italic', color: 'text.disabled' }) }}>
                        {rawName || t('mailTemplate.employeeSelection.unknownContact')}
                      </Typography>
                      {contact.position && (
                        <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.disabled', fontWeight: 400 }}>
                          {contact.position}
                        </Typography>
                      )}
                    </Box>
                    {hasEmail && (
                      <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                        {contact.emailAddress}
                      </Typography>
                    )}
                  </>
                  )
              }
            </Box>
          </MenuItem>
        )
      })}
    </Box>
  )
}

const ContactsDropdown = ({
  genericInfo, extendedInfo,
  genericContacts = [], extendedContacts = [],
  onContactToggle, onPremiumContactToggle
}) => {
  const { t } = useTranslation()
  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)

  const hasAnyContacts = genericContacts.length > 0 || extendedContacts.length > 0
  const canOpen = hasAnyContacts && (genericInfo?.status === 'DONE' || extendedInfo?.status === 'DONE')

  const handleOpen = (e) => {
    e.stopPropagation()
    if (canOpen) setAnchorEl(e.currentTarget)
  }

  const handleClose = (e) => {
    e?.stopPropagation?.()
    setAnchorEl(null)
  }

  return (
    <>
      <Box
        onClick={handleOpen}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          cursor: canOpen ? 'pointer' : 'default',
          transition: 'background-color 0.15s',
          px: 0.5,
          py: 0.75,
          borderRadius: 1,
          '&:hover': canOpen ? { backgroundColor: 'rgba(0, 0, 0, 0.04)' } : {}
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, width: 110 }}>
          <AlternateEmail sx={{ fontSize: 18, color: genericInfo.color, flexShrink: 0 }} />
          <Typography variant="caption" sx={{ color: genericInfo.color, fontSize: '0.75rem', fontWeight: 500, whiteSpace: 'nowrap' }}>
            {genericInfo.label}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, width: 130 }}>
          {extendedInfo && (
            <>
              <PersonSearch sx={{ fontSize: 18, color: extendedInfo.color, flexShrink: 0 }} />
              <Typography variant="caption" sx={{ color: extendedInfo.color, fontSize: '0.75rem', fontWeight: 500, whiteSpace: 'nowrap' }}>
                {extendedInfo.label}
              </Typography>
            </>
          )}
        </Box>

        <KeyboardArrowDown sx={{ fontSize: 20, color: 'text.disabled', flexShrink: 0, visibility: canOpen ? 'visible' : 'hidden' }} />
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
        slotProps={{
          paper: {
            sx: {
              minWidth: 320,
              maxWidth: 400,
              maxHeight: 420,
              mt: 0.5,
              borderRadius: 2,
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
            }
          }
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {genericContacts.length > 0 && (
          <ContactListSection icon={AlternateEmail} title={t('mailTemplate.campaigns.dashboard.firmRow.emails')} contacts={genericContacts} onToggle={onContactToggle} emailOnly />
        )}
        {genericContacts.length > 0 && extendedContacts.length > 0 && <Divider sx={{ my: 0.5 }} />}
        {extendedContacts.length > 0 && (
          <ContactListSection icon={PersonSearch} title={t('mailTemplate.campaigns.dashboard.firmRow.contacts')} contacts={extendedContacts} onToggle={onPremiumContactToggle} />
        )}
      </Menu>
    </>
  )
}

const CONTACT_ROW_PL = '90px'
const EMAIL_GRID = '28px 1fr 110px 32px'
const CONTACT_GRID = '28px 1fr 1fr 110px 32px'

const ContactRow = ({ contact, isLast, onPreview, onToggle, isFirstContact }) => {
  const { t } = useTranslation()
  const contactName = [contact.firstName, contact.lastName].filter(Boolean).join(' ')
  const hasEmail = Boolean(contact.emailAddress)
  const hasContent = Boolean(contact.emailContent)
  const isEdited = Boolean(contact.emailContentEdited)

  const contentStatus = useMemo(() => {
    if (!hasEmail) return { labelKey: 'mailTemplate.campaigns.dashboard.firmRow.noEmail', color: '#B71C1C' }
    if (isEdited) return { labelKey: 'mailTemplate.campaigns.dashboard.firmRow.edited', color: '#1565C0' }
    if (hasContent) return { labelKey: 'mailTemplate.campaigns.dashboard.filter.ready', color: '#2E7D32' }
    return { labelKey: 'mailTemplate.campaigns.dashboard.firmRow.notGenerated', color: '#9E9E9E' }
  }, [hasEmail, hasContent, isEdited])

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: CONTACT_GRID,
        alignItems: 'center',
        gap: 1.5,
        pl: CONTACT_ROW_PL,
        pr: 2,
        py: 1.5,
        borderBottom: isLast ? 'none' : '1px solid',
        borderColor: 'divider',
        '&:hover': { backgroundColor: 'action.hover' },
        transition: 'background-color 0.15s'
      }}
    >
      <Checkbox size="small" checked={Boolean(contact.selected)} onChange={() => onToggle?.(contact.id)} sx={{ p: 0.5 }} />

      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, ...(!contactName && { fontStyle: 'italic', color: 'text.disabled' }) }} noWrap>
          {contactName || t('mailTemplate.employeeSelection.unknownContact')}
        </Typography>
        {contact.position && (
          <Typography variant="caption" color="text.secondary" noWrap>
            {contact.position}
          </Typography>
        )}
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" color="text.secondary" noWrap>
          {contact.emailAddress || '\u2014'}
        </Typography>
      </Box>

      <StatusCard {...contentStatus} />

      <Tooltip title={t('mailTemplate.campaigns.dashboard.firmRow.previewEmail')}>
        <IconButton size="small" disabled={!hasContent} onClick={() => onPreview?.(contact)} {...(isFirstContact && { 'data-tour': 'campaign-preview-email' })}>
          <Visibility sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
    </Box>
  )
}

const EmailRow = ({ contact, isLast, onPreview, onToggle, isFirstContact }) => {
  const { t } = useTranslation()
  const hasEmail = Boolean(contact.emailAddress)
  const hasContent = Boolean(contact.emailContent)
  const isEdited = Boolean(contact.emailContentEdited)

  const contentStatus = useMemo(() => {
    if (!hasEmail) return { labelKey: 'mailTemplate.campaigns.dashboard.firmRow.noEmail', color: '#B71C1C' }
    if (isEdited) return { labelKey: 'mailTemplate.campaigns.dashboard.firmRow.edited', color: '#1565C0' }
    if (hasContent) return { labelKey: 'mailTemplate.campaigns.dashboard.filter.ready', color: '#2E7D32' }
    return { labelKey: 'mailTemplate.campaigns.dashboard.firmRow.notGenerated', color: '#9E9E9E' }
  }, [hasEmail, hasContent, isEdited])

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: EMAIL_GRID,
        alignItems: 'center',
        gap: 1.5,
        pl: CONTACT_ROW_PL,
        pr: 2,
        py: 1.5,
        borderBottom: isLast ? 'none' : '1px solid',
        borderColor: 'divider',
        '&:hover': { backgroundColor: 'action.hover' },
        transition: 'background-color 0.15s'
      }}
    >
      <Checkbox size="small" checked={Boolean(contact.selected)} onChange={() => onToggle?.(contact.id)} sx={{ p: 0.5 }} />

      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" color="text.secondary" noWrap>
          {contact.emailAddress || '\u2014'}
        </Typography>
      </Box>

      <StatusCard {...contentStatus} />

      <Tooltip title={t('mailTemplate.campaigns.dashboard.firmRow.previewEmail')}>
        <IconButton size="small" disabled={!hasContent} onClick={() => onPreview?.(contact)} {...(isFirstContact && { 'data-tour': 'campaign-preview-email' })}>
          <Visibility sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
    </Box>
  )
}

const CampaignFirmRow = ({
  firm,
  isExpanded,
  isSelected,
  isFirstRow,
  onToggleExpand,
  onToggleSelect,
  onContactToggle,
  onPremiumContactToggle,
  onEmailPreview,
  onDelete
}) => {
  const { t } = useTranslation()
  const [menuAnchor, setMenuAnchor] = useState(null)

  const website = extractWebsite(firm.web)
  const allContacts = useMemo(() => firm.contacts || [], [firm.contacts])
  const allPremiumContacts = useMemo(() => firm.premiumContacts || [], [firm.premiumContacts])

  const { genericContacts, selectedPremiumContacts, selectedContacts } = useMemo(() => {
    const generic = allContacts.filter((c) => c.selected)
    const premium = allPremiumContacts.filter((c) => c.selected)
    return { genericContacts: generic, selectedPremiumContacts: premium, selectedContacts: [...generic, ...premium] }
  }, [allContacts, allPremiumContacts])

  const relevancyBadge = useMemo(() => {
    if (firm.relevancyStatus === 'IN_PROGRESS') return RELEVANCY_STATUS.IN_PROGRESS
    if (firm.suitabilityLevel) return RELEVANCY_STATUS.DONE(firm.suitabilityLevel)
    return RELEVANCY_STATUS.NOT_STARTED
  }, [firm.relevancyStatus, firm.suitabilityLevel])

  const genericContactInfo = useMemo(() => {
    if (firm.genericContactStatus === 'IN_PROGRESS') {
      return { label: t('mailTemplate.campaigns.dashboard.firmRow.querying'), color: '#1565C0', status: 'IN_PROGRESS' }
    }
    if (firm.genericContactStatus === 'DONE') {
      const total = allContacts.length
      const selected = genericContacts.length
      if (total === 0) return { label: t('mailTemplate.campaigns.dashboard.firmRow.noEmails'), color: '#E65100', status: 'DONE' }
      return { label: t('mailTemplate.campaigns.dashboard.firmRow.emailCount', { selected, total }), color: '#2E7D32', status: 'DONE' }
    }
    return { label: t('mailTemplate.campaigns.dashboard.firmRow.notQueried'), color: '#9E9E9E', status: 'NOT_STARTED' }
  }, [firm.genericContactStatus, allContacts.length, genericContacts.length, t])

  const premiumContactInfo = useMemo(() => {
    if (firm.premiumContactStatus === 'IN_PROGRESS') {
      return { label: t('mailTemplate.campaigns.dashboard.firmRow.searching'), color: '#1565C0', status: 'IN_PROGRESS' }
    }
    if (firm.premiumContactStatus === 'DONE') {
      const total = allPremiumContacts.length
      const selected = selectedPremiumContacts.length
      if (total === 0) return { label: t('mailTemplate.campaigns.dashboard.firmRow.noContacts'), color: '#E65100', status: 'DONE' }
      return { label: t('mailTemplate.campaigns.dashboard.firmRow.contactCount', { selected, total }), color: '#2E7D32', status: 'DONE' }
    }
    return { label: t('mailTemplate.campaigns.dashboard.firmRow.notSearched'), color: '#9E9E9E', status: 'NOT_STARTED' }
  }, [firm.premiumContactStatus, allPremiumContacts.length, selectedPremiumContacts.length, t])

  const contentBadge = useMemo(() => {
    if (firm.contentStatus === 'IN_PROGRESS') return CONTENT_STATUS.IN_PROGRESS
    if (firm.contentStatus === 'DONE') return CONTENT_STATUS.DONE(firm.contentReadyCount || 0)
    return CONTENT_STATUS.NOT_STARTED
  }, [firm.contentStatus, firm.contentReadyCount])

  return (
    <Box
      sx={{
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-child': { borderBottom: 'none' }
      }}
    >
      <Box
        onClick={onToggleExpand}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
          px: 2,
          py: 2,
          cursor: 'pointer',
          transition: 'background-color 0.15s',
          '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.02)' },
          ...(isExpanded && { backgroundColor: 'rgba(15, 198, 185, 0.03)' })
        }}
      >
        <Box sx={{ width: 32, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
          <IconButton
            size="small"
            sx={{
              p: 0.5,
              color: isExpanded ? 'primary.main' : 'text.secondary'
            }}
          >
            {isExpanded
              ? <KeyboardArrowDown sx={{ fontSize: 20 }} />
              : <KeyboardArrowRight sx={{ fontSize: 20 }} />
            }
          </IconButton>
        </Box>

        <Checkbox
          size="small"
          checked={isSelected}
          onClick={(e) => e.stopPropagation()}
          onChange={onToggleSelect}
          sx={{ p: 0.5 }}
        />

        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                {firm.name}
              </Typography>
              {website && (
                <Link
                  href={website.startsWith('http') ? website : `https://${website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.25,
                    color: 'text.secondary',
                    textDecoration: 'none',
                    fontSize: '0.75rem',
                    '&:hover': { color: 'primary.main' }
                  }}
                >
                  <Language sx={{ fontSize: 14 }} />
                  {website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                </Link>
              )}
            </Box>

            {firm.explanation && (
              <Tooltip title={isExpanded ? '' : firm.explanation} arrow placement="bottom-start" enterDelay={500}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    fontSize: '0.8rem',
                    lineHeight: 1.5,
                    display: '-webkit-box',
                    WebkitLineClamp: isExpanded ? 'unset' : 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}
                >
                  {firm.explanation}
                </Typography>
              </Tooltip>
            )}
          </Box>

          <Box
            sx={{
              display: 'flex',
              gap: 0.75,
              flexShrink: 0,
              alignItems: 'stretch'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <RelevancyBadge badge={relevancyBadge} />
            <Box {...(isFirstRow && { 'data-tour': 'campaign-badge-contacts' })}>
              <ContactsDropdown
                genericInfo={genericContactInfo}
                extendedInfo={premiumContactInfo}
                genericContacts={allContacts}
                extendedContacts={allPremiumContacts}
                onContactToggle={(contactId) => onContactToggle?.(firm.id, contactId)}
                onPremiumContactToggle={(contactId) => onPremiumContactToggle?.(firm.id, contactId)}
              />
            </Box>
            <Box {...(isFirstRow && { 'data-tour': 'campaign-badge-content' })}>
              <StatusCard icon={MailOutline} {...contentBadge} />
            </Box>
            <IconButton
              size="small"
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              sx={{
                p: 0.5,
                color: 'text.disabled',
                '&:hover': { color: 'text.secondary' }
              }}
              {...(isFirstRow && { 'data-tour': 'campaign-firm-menu' })}
            >
              <MoreVert sx={{ fontSize: 18 }} />
            </IconButton>
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={() => setMenuAnchor(null)}
              onClick={(e) => e.stopPropagation()}
              slotProps={{
                paper: {
                  sx: { minWidth: 140, borderRadius: 2, boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }
                }
              }}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem
                onClick={() => { setMenuAnchor(null); onDelete?.(firm) }}
                dense
                sx={{ color: 'error.main', fontSize: '0.8rem' }}
              >
                <ListItemIcon sx={{ minWidth: 28 }}>
                  <DeleteOutline sx={{ fontSize: 16, color: 'error.main' }} />
                </ListItemIcon>
                <ListItemText primaryTypographyProps={{ fontSize: '0.8rem' }}>
                  {t('mailTemplate.campaigns.dashboard.firmRow.deleteFirm')}
                </ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </Box>

      </Box>

      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <Box
          sx={{
            backgroundColor: 'rgba(0, 0, 0, 0.01)',
            borderTop: '1px solid',
            borderColor: 'divider'
          }}
        >
          {selectedContacts.length > 0
            ? (
              <>
                {genericContacts.length > 0 && (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, pl: CONTACT_ROW_PL, pr: 2, pt: 1.5, pb: 0.5 }}>
                      <AlternateEmail sx={{ fontSize: 14, color: 'text.secondary' }} />
                      <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', letterSpacing: 0.5, fontSize: '0.7rem' }}>
                        {t('mailTemplate.campaigns.dashboard.firmRow.emails')}
                      </Typography>
                    </Box>
                    {genericContacts.map((contact, idx) => (
                      <EmailRow
                        key={contact.id}
                        contact={contact}
                        isLast={selectedPremiumContacts.length === 0 && idx === genericContacts.length - 1}
                        isFirstContact={isFirstRow && idx === 0}
                        onPreview={(c) => onEmailPreview?.({ contact: c, firm })}
                        onToggle={(contactId) => onContactToggle?.(firm.id, contactId)}
                      />
                    ))}
                  </>
                )}
                {selectedPremiumContacts.length > 0 && (
                  <>
                    {genericContacts.length > 0 && <Divider />}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, pl: CONTACT_ROW_PL, pr: 2, pt: 1.5, pb: 0.5 }}>
                      <PersonSearch sx={{ fontSize: 14, color: 'text.secondary' }} />
                      <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', letterSpacing: 0.5, fontSize: '0.7rem' }}>
                        {t('mailTemplate.campaigns.dashboard.firmRow.contacts')}
                      </Typography>
                    </Box>
                    {selectedPremiumContacts.map((contact, idx) => (
                      <ContactRow
                        key={contact.id}
                        contact={contact}
                        isLast={idx === selectedPremiumContacts.length - 1}
                        isFirstContact={isFirstRow && genericContacts.length === 0 && idx === 0}
                        onPreview={(c) => onEmailPreview?.({ contact: c, firm })}
                        onToggle={(contactId) => onPremiumContactToggle?.(firm.id, contactId)}
                      />
                    ))}
                  </>
                )}
              </>
              )
            : (
              <Box sx={{ px: 3, py: 3, pl: 8, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {t('mailTemplate.campaigns.dashboard.firmRow.noContactsFound')}
                </Typography>
              </Box>
              )
          }
        </Box>
      </Collapse>
    </Box>
  )
}

export default CampaignFirmRow
